import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { Modal, Btn, Alert } from './UI';
import { raiffeisenApi } from '../services/api';

/* Raiffeisen consent-lejárat: NAPONTA EGYSZER leokézandó figyelmeztetés.
 *
 * Miért modal és miért naponta: a consent **max 60 nap és NEM újítható**, lejáratkor
 * pedig az UTALÁS, az EGYENLEG és a KIVONAT-SZINKRON is megáll. A tanúsítványra volt
 * 30 napos jelzés (egy 2028-as lejáratra!), a consentre viszont SEMMI — pedig az jár le
 * hamarabb. Egy panelen elrejtett sárga sávot könnyű nem észrevenni; egy nyugtázandó
 * modal nem az. Naponta 1×, hogy ne legyen belőle zaj.
 *
 * A nyugtázás a localStorage-ban, DÁTUMRA kötve tárolódik (`...:2026-08-05`), tehát
 * másnap újra megjelenik. Szándékosan nem szerver-oldali: ez emlékeztető, nem audit —
 * és így nem kell hozzá se tábla, se végpont.
 */
const ACK_PREFIX = 'phenofin.raiffeisenConsentWarnAck:';

const dFmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;
};

export default function ConsentExpiryWarning() {
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['raiffeisen-consent-expiry'],
    queryFn: raiffeisenApi.consentExpiry,
    // Nem kritikus út: ha a Raiffeisen nincs konfigurálva, a hívás hibázhat — akkor
    // egyszerűen nincs figyelmeztetés (retry: false, hogy ne pörögjön).
    retry: false,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!data?.warn) return;
    const today = new Date().toISOString().slice(0, 10);
    let acked = false;
    try { acked = localStorage.getItem(ACK_PREFIX + today) === '1'; } catch { /* privát mód */ }
    if (!acked) setOpen(true);
  }, [data]);

  const ack = () => {
    const today = new Date().toISOString().slice(0, 10);
    try { localStorage.setItem(ACK_PREFIX + today, '1'); } catch { /* privát mód */ }
    setOpen(false);
  };

  if (!data?.warn) return null;

  const expired = data.expired || [];
  const expiring = data.expiring || [];

  return (
    <Modal open={open} onClose={ack} width={520} accentColor="#f59e0b"
      title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ShieldAlert size={16} /> Raiffeisen felhatalmazás (consent) lejárata
      </span>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        {expired.length > 0 && (
          <Alert type="danger">
            <strong>{expired.length} felhatalmazás LEJÁRT.</strong> A Raiffeisen utalás,
            egyenleg-lekérdezés és kivonat-szinkron addig nem működik, amíg nincs új,
            jóváhagyott consent.
          </Alert>
        )}
        {expiring.length > 0 && (
          <Alert type="warning">
            <strong>{expiring.length} felhatalmazás {data.days_left} napon belül lejár.</strong>{' '}
            A consent legfeljebb 60 napos és <b>nem újítható meg</b> — újat kell létrehozni
            és a Pay Portálon jóváhagyatni.
          </Alert>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[...expired, ...expiring].map(c => (
            <div key={c.id} style={{
              border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              padding: '0.5rem 0.6rem', fontSize: '0.78rem',
              display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
            }}>
              <span style={{ fontFamily: 'var(--mono)' }}>
                #{c.bank_consent_id}
                <span style={{ color: 'var(--text-muted)' }}>
                  {' · '}{(c.account_list || []).length} számla
                </span>
              </span>
              <span>
                {dFmt(c.expiry_date)}{' '}
                <b style={{ color: c.days_left < 0 ? 'var(--danger)' : '#f59e0b' }}>
                  ({c.days_left < 0 ? 'lejárt' : `${c.days_left} nap`})
                </b>
              </span>
            </div>
          ))}
        </div>

        <p style={{ fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
          Teendő: <b>Beállítások → Bankszámlák → API kapcsolat → Raiffeisen</b> alatt hozz létre
          új consentet, majd az aláíró hagyja jóvá a Pay Portálon. Ez a figyelmeztetés naponta
          egyszer jelenik meg, amíg a lejárat közel van.
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Btn onClick={ack}>Értem</Btn>
        </div>
      </div>
    </Modal>
  );
}
